import { BaseCategory } from "./BaseCategory";
import { AuthoringBlockType, ConnectorType, ElementTextBlockPositionType, HeaderPositionType, NodeType, PaletteColorType, TextStyleType } from "../constants";
import { SlideCategories } from "./CategoryController";
import { SlideModelBuilder } from "./SlideModelBuilder";

export class HierarchyDiagramCategory extends BaseCategory {
    get name() {
        return "Hierarchy Diagram";
    }

    get type() {
        return SlideCategories.HIERARCHY_DIAGRAM;
    }

    get icon() {
        return null;
    }

    get description() {
        return "The content describes a central concept with related items radiating outward, or a hub-and-spoke relationship. Use this for showing connections between a central idea and supporting elements, organizational structures, or hierarchical relationships.";
    }

    get slideTypes() {
        return [];
    }

    getPrompt() {
        return `Parse the content to identify a central hub concept and its related spoke items. The hub should be the main/central concept, and items should be the connected elements.`;
    }

    getImagePrompt() {
        return false;
    }

    getModelHints() {
        return {
            diagramType: "hub and spoke | hierarchy | organizational structure",
        };
    }

    getPromptSchema(options) {
        return {
            hub: "string for the center hub item (the main/central concept)",
            items: this.getItemListSchema(options),
            conclusion: "an optional short conclusion or takeaway for the slide",
        };
    }

    getSlideModels(model) {
        let models = [
            this.buildHubAndSpokeWithIcons(model),
            this.buildHubAndSpoke(model),
        ];

        return models;
    }

    buildHubAndSpoke(model) {
        let builder = new SlideModelBuilder("hub_and_spoke", model);
        builder.createHeader({ heading: model.slideTitle, position: HeaderPositionType.TOP });

        // Create hub node
        const hubModel = {
            id: "hub",
            nodeType: NodeType.CIRCLE,
            color: PaletteColorType.THEME,
            textWidth: 100,
            text: model.hub ? {
                blocks: [{
                    html: model.hub,
                    textStyle: TextStyleType.TITLE,
                    type: AuthoringBlockType.TEXT,
                }]
            } : {
                blocks: [{
                    html: "",
                    textStyle: TextStyleType.TITLE,
                    type: AuthoringBlockType.TEXT,
                }]
            }
        };

        // Create spoke items
        const items = model.items.map(item => ({
            nodeType: NodeType.TEXT,
            text: {
                blocks: [
                    builder.createBlock(item.title, TextStyleType.TITLE),
                    // builder.createBlock(item.description, TextStyleType.BODY),
                ]
            }
        }));

        // Create connectors from hub to each spoke
        const connectors = {
            items: items.map((item, index) => ({
                source: "hub",
                target: null, // Will be set by the element based on item ids
                connectorType: ConnectorType.STRAIGHT,
                startDecoration: "none",
                endDecoration: "arrow",
            }))
        };

        let primary = {
            hub: hubModel,
            items: items,
            connectors: connectors,
            collectionColor: "colorful",
            connectorStyle: "hub",
            useAlternateRotation: true,
            ellipse_size: 1.9
        };

        builder.createPrimary(primary);

        if (model.conclusion) {
            builder.createBottomTray(model.conclusion);
        }

        return builder.model;
    }

    buildHubAndSpokeWithIcons(model) {
        let builder = new SlideModelBuilder("hub_and_spoke", model);
        builder.createHeader({ heading: model.slideTitle, position: HeaderPositionType.TOP });

        // Create hub node
        const hubModel = {
            id: "hub",
            nodeType: NodeType.CIRCLE,
            color: PaletteColorType.THEME,
            textWidth: 100,
            text: model.hub ? {
                blocks: [{
                    html: model.hub,
                    textStyle: TextStyleType.TITLE,
                    type: AuthoringBlockType.TEXT,
                }]
            } : {
                blocks: [{
                    html: "",
                    textStyle: TextStyleType.TITLE,
                    type: AuthoringBlockType.TEXT,
                }]
            }
        };

        // Create spoke items with icons
        const items = model.items.map(item => ({
            nodeType: NodeType.CONTENT_AND_TEXT,
            ...builder.createIcon(item.icon ?? "star"),
            text: {
                blocks: [
                    builder.createBlock(item.title, TextStyleType.TITLE),
                    builder.createBlock(item.description, TextStyleType.BODY),
                ]
            }
        }));

        // Create connectors from hub to each spoke
        const connectors = {
            items: items.map((item, index) => ({
                source: "hub",
                target: null, // Will be set by the element based on item ids
                connectorType: ConnectorType.STRAIGHT,
                startDecoration: "none",
                endDecoration: "arrow",
            }))
        };

        let primary = {
            hub: hubModel,
            items: items,
            connectors: connectors,
            collectionColor: "colorful",
            connectorStyle: "hub",
            useAlternateRotation: true,
            ellipse_size: 1.9
        };

        builder.createPrimary(primary);

        if (model.conclusion) {
            builder.createBottomTray(model.conclusion);
        }

        return builder.model;
    }
}
